import React, { useState, useContext, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  routeTop, routeTopJa, routeTopPt, routeTopEs,
  routeTopKo, routeTopHi, routeTopPl,
  routeAppApi,
  routeTermOfService, routePrivacyPolicy, routeTokutei,
  routeContact, routeContactDone,
} from '../../../style/css/CmnStyle';
import { hpContext } from '../../hp/HPContext';

// 翻訳リソース未読み込み時のデフォルト値（public/index.htmlの静的な値と同一にし、
// 読み込み中にキー名がそのまま表示されるのを防ぐ）
const DEFAULT_TITLE = 'Skaters - See the moment of the Trick';
const DEFAULT_DESCRIPTION = "Exclusive app for skaters, 'Skaters'. Never miss a moment of tricks! Enjoy your skateboarding life while improving and finding friends! Download the app and give it a try.";
const DEFAULT_KEYWORDS = 'Skaters,skaters,App,app,iOS,Android,SmartPhone,Skate,skate,SkateBoard,skateboard,Skateboarding,skateboarding,Skater,skater,improvement,tricks,trick,friends,scroll,swipe,compare,moment';

// SeoHeadTagが個別に案内を出し分けるルート以外はすべて未定義URL＝404扱いにする
const knownPaths = [
  routeTop, routeTopJa, routeTopPt, routeTopEs,
  routeTopKo, routeTopHi, routeTopPl,
  routeAppApi,
  routeTermOfService, routePrivacyPolicy, routeTokutei,
  routeContact, routeContactDone,
];


function SeoHeadTag() {
  const { t, i18n } = useTranslation();
  // 現在のパス
  const location = useLocation();
  const currentPath = location.pathname;

  const langCode = useContext(hpContext).langCode;

  // 翻訳リソース読み込み前は public/index.html の静的な値と同じ内容にしておく。
  // t()にデフォルト値を渡さないと、リソース未読み込み時にキー名（例: "seo.main.title"）が
  // 一瞬そのまま表示されてしまう
  const [title, setTitle] = useState(t('seo.main.title', DEFAULT_TITLE));
  const [discription, setDiscription] = useState(t('seo.main.discription', DEFAULT_DESCRIPTION));
  const [keywords, setKeyowrds] = useState(t('seo.main.keywords', DEFAULT_KEYWORDS));
  const [robots, setRobots] = useState('all');


  // 遷移・言語切替ごとのSEO内容変更
  // i18n.changeLanguage()は非同期のため、Hp.jsのlangCode（setLangCode直後の値）ではなく
  // 実際に翻訳リソースの切り替えが完了した i18n.language を見て発火させる
  useEffect(() => {
    // デフォルト
    let newTitle = t('seo.main.title', DEFAULT_TITLE);
    let newDiscription = t('seo.main.discription', DEFAULT_DESCRIPTION);
    let newKeywords = t('seo.main.keywords', DEFAULT_KEYWORDS);
    let newRobots = 'all';

    switch(currentPath) {
      case routeContact:
        // お問い合わせ
        newTitle = t('seo.contact.title', 'Skaters - Contact');
        newDiscription = t('seo.contact.discription', DEFAULT_DESCRIPTION);
        newKeywords = t('seo.contact.keywords', DEFAULT_KEYWORDS);
        break;
      case routeTermOfService:
        // 利用規約
        newTitle = `Skaters - ${t('kiyaku.title', 'Terms of Service')}`;
        newDiscription = t('seo.terms.discription',
          'Terms of Service for the Skaters app, the exclusive social platform for skateboarders.');
        break;
      case routePrivacyPolicy:
        // プライバシーポリシー
        newTitle = `Skaters - ${t('privacy.title', 'Privacy Policy')}`;
        newDiscription = t('seo.privacy.discription',
          'Privacy Policy for the Skaters app, explaining how we handle your personal information.');
        break;
      case routeTokutei:
        // 特定商取引法に基づく表記
        newTitle = `Skaters - ${t('tokusyou.title', 'Specific Commercial Transactions Act Disclosure')}`;
        newDiscription = t('seo.tokutei.discription',
          "Legal disclosure under Japan's Specified Commercial Transactions Act for the Skaters app.");
        break;
      default:
        if (!knownPaths.includes(currentPath)) {
          // 未定義URL（404）。検索結果に出さないようnoindexにする
          newTitle = `Skaters - ${t('err.404.title', '404 NOT FOUND')}`;
          newDiscription = t('err.404.discription', 'Page not found.');
          newRobots = 'noindex';
        }
    }

    setTitle(newTitle);
    setDiscription(newDiscription);
    setKeyowrds(newKeywords);
    setRobots(newRobots);
  }, [i18n.language, currentPath])


  return (
    <Helmet defer={false}>
      <html lang={i18n.language || langCode || 'en'} />
      <title>{title}</title>
      <meta name="keywords" content={keywords} />
      <meta name="description" content={discription} />
      <meta name="robots" content={robots} />

      <meta property="og:site_name" content={title} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={discription} />
      <meta property="og:url" content="https://skaters.cloud/" />

      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={discription} />
    </Helmet>
  );
}

export default SeoHeadTag;